/**
 * Client-safe OC org-setting write validation (super-admin + shared dispatch).
 * Keeps D04a boundary: no Prisma, no ~/server/*, no UI modules.
 * @see docs/OPT_IN_ORDER_DOCUMENTS.md § Sales Order Confirmation
 */
import { hasEditorHtmlContent } from "~/shared/documents/schemas/bol-document-config.schema";
import {
  emptyOcDocumentModalSlots,
  type OcDocumentConfig,
  OcDocumentConfigSchema,
} from "~/shared/documents/schemas/oc-document-config.schema";
import { assertEditorSettingString, EditorSchema } from "~/utils/types/rich-text";

/** Legacy OC config keys before SC-2345 field renames — shared by fail-soft parse and strict writes. */
export const migrateLegacyOcDocumentConfig = (parsed: unknown): unknown => {
  if (typeof parsed !== "object" || parsed == null || Array.isArray(parsed)) {
    return parsed;
  }
  const record = { ...(parsed as Record<string, unknown>) };

  const legacyDrumAllow = record.includeDrumHazardPage === true || record.modalDrumHazardOverride === true;
  if (legacyDrumAllow && record.allowOptionalDrumHazardPage !== true) {
    record.allowOptionalDrumHazardPage = true;
  }
  delete record.includeDrumHazardPage;
  delete record.modalDrumHazardOverride;

  if (!("showOrderConfirmationNoteBlock" in record)) {
    const legacyCombinedNotesEquipmentBlock = record.showEquipmentRequiredBlock === true;
    record.showOrderConfirmationNoteBlock = record.modalOrderConfirmationNote === true || legacyCombinedNotesEquipmentBlock;
  }

  delete record.showCarrierColumn;
  delete record.showSalesPersonBlock;
  delete record.defaultModeOfTransport;
  delete record.defaultContainerType;
  delete record.defaultEquipmentRequired;
  delete record.showModeOfTransport;
  delete record.showPkgContainerType;
  delete record.showEquipmentRequiredBlock;
  delete record.modalOrderConfirmationNote;
  delete record.modalEquipmentRequired;
  delete record.modalModeOfTransport;
  delete record.modalContainerType;

  const slots = {
    ...emptyOcDocumentModalSlots(),
    ...(typeof record.modalSlots === "object" && record.modalSlots != null && !Array.isArray(record.modalSlots)
      ? (record.modalSlots as Record<string, unknown>)
      : {}),
  };

  const hadModalSlotsKey = "modalSlots" in record;
  if (!hadModalSlotsKey) {
    if (record.showOrderConfirmationNoteBlock === true) {
      slots.orderConfirmationNote = true;
    }
    if (record.showProducer === true) {
      slots.lineProducer = true;
    }
    if (record.showQuantityUnits === true) {
      slots.lineQuantityUnits = true;
    }
  }
  record.modalSlots = slots;

  if (!("showMsdsDisclaimer" in record)) {
    const msdsParsed = EditorSchema.safeParse(record.msdsDisclaimerFooter);
    if (msdsParsed.success && hasEditorHtmlContent(msdsParsed.data)) {
      record.showMsdsDisclaimer = true;
    }
  }

  if (record.includeCustomerPickupOnPdf === true && record.allowCustomerPickup !== true) {
    record.allowCustomerPickup = true;
  }
  delete record.includeCustomerPickupOnPdf;

  if (!("showDrumHazardOnPdf" in record) && record.allowOptionalDrumHazardPage === true) {
    record.showDrumHazardOnPdf = true;
  }

  return record;
};

/** Parse migrated JSON for writes (super-admin / strict boundaries); throws on invalid shape. */
export const parseOcDocumentConfigFromUnknown = (parsed: unknown): OcDocumentConfig => {
  const migrated = migrateLegacyOcDocumentConfig(parsed);
  return OcDocumentConfigSchema.parse(migrated);
};

/** Rich-text editor JSON sanity check for OC org settings writes. */
export const assertOcDocumentConfigEditorSettings = (config: OcDocumentConfig): void => {
  assertEditorSettingString(JSON.stringify(config.msdsDisclaimerFooter));
  assertEditorSettingString(JSON.stringify(config.drumHazardWarningBody));
  assertEditorSettingString(JSON.stringify(config.defaultPickupNote));
};

/** Super-admin org setting overwrite for OC_DOCUMENT_CONFIG. */
export const assertOcDocumentOrgSettingValueForWrite = (value: string): void => {
  let parsed: unknown;
  try {
    parsed = JSON.parse(value);
  } catch {
    throw new Error("Invalid OC document config JSON");
  }
  const config = parseOcDocumentConfigFromUnknown(parsed);
  assertOcDocumentConfigEditorSettings(config);
};
